import { Controller } from '@hotwired/stimulus'
import i18n from '../javascripts/util/i18n'

// Progressively enhances a native <select> into a searchable dropdown.
// The real <select> stays in the DOM (hidden) so the form submits normally;
// this just provides a type-to-filter UI and a consistently-aligned chevron.
//
// Markup: a wrapper with the hidden select + a button (label + chevron) + a
// panel containing a search input and a list the controller populates.
export default class extends Controller {
  static targets = ['select', 'button', 'label', 'panel', 'search', 'list']
  static values = { placeholder: String, optionsSource: String, remoteUrl: String, blankChoice: Boolean, panelMinWidth: Number }

  connect() {
    this.loadSharedOptions()
    this.refreshOptions()
    this.renderLabel()
    this.boundDocClick = this.onDocClick.bind(this)
    document.addEventListener('click', this.boundDocClick)
    // Some selects are repopulated at runtime — a dependent dropdown synced by
    // another controller (e.g. shops-form rebuilding template options), or a
    // turbo_stream appending/removing <option>s while the form is open. Re-read
    // the options and refresh the label/list so the styled control never goes stale.
    this.optionObserver = new MutationObserver(() => {
      this.refreshOptions()
      this.renderLabel()
      if (!this.panelTarget.hidden) {
        this.renderList(this.hasSearchTarget ? this.searchTarget.value.trim().toLowerCase() : '')
      }
    })
    this.optionObserver.observe(this.selectTarget, { childList: true, subtree: true })
  }

  // When `optionsSource` names a <script type="application/json"> holding
  // [[label, value], …], the dropdown list is built from that ONE shared blob
  // instead of embedding every option in each row's <select> (which bloats the
  // page when there are hundreds of rows). Each <select> then only carries its
  // own selected <option>.
  loadSharedOptions() {
    this.sharedOptions = null
    const id = this.optionsSourceValue
    if (!id) return
    const el = document.getElementById(id)
    if (!el) return
    try {
      this.sharedOptions = JSON.parse(el.textContent).map(([label, value]) => ({
        value: String(value), label: String(label), blank: false,
      }))
    } catch (_e) {
      this.sharedOptions = null
    }
  }

  refreshOptions() {
    const selectOpts = Array.from(this.selectTarget.options).map((o) => ({
      value: o.value, label: o.text, blank: o.value === '',
    }))
    if (!this.sharedOptions) {
      this.options = selectOpts
      return
    }
    // Shared list + a blank + any preselected value not in the shared list
    // (e.g. a key from a module no longer assigned), so it stays selectable.
    const sharedValues = new Set(this.sharedOptions.map((o) => o.value))
    const blank = selectOpts.filter((o) => o.blank)
    const extra = selectOpts.filter((o) => !o.blank && !sharedValues.has(o.value))
    this.options = [...blank, ...extra, ...this.sharedOptions]
  }

  // Ensure the real <select> carries the chosen value as an <option> so the form
  // submits it (the shared-source <select> starts with only its preselected one).
  ensureSelectOption(value) {
    if (Array.from(this.selectTarget.options).some((o) => o.value === value)) return
    const match = this.options.find((o) => o.value === value)
    const opt = document.createElement('option')
    opt.value = value
    opt.text = match ? match.label : value
    this.selectTarget.appendChild(opt)
  }

  disconnect() {
    document.removeEventListener('click', this.boundDocClick)
    this.optionObserver?.disconnect()
    if (this.boundReposition) {
      window.removeEventListener('scroll', this.boundReposition, true)
      window.removeEventListener('resize', this.boundReposition)
      this.boundReposition = null
    }
  }

  readOptions() {
    this.refreshOptions()
  }

  // Re-sync after the underlying <select> options are rebuilt by another
  // controller (e.g. shops-form repopulating templates when the module changes).
  refresh() {
    this.readOptions()
    this.renderLabel()
    if (!this.panelTarget.hidden) this.filter()
  }

  toggle(event) {
    event.preventDefault()
    this.panelTarget.hidden ? this.open() : this.close()
  }

  open() {
    this.panelTarget.hidden = false
    // The search input is optional — non-searchable selects render the styled
    // dropdown (single caret + click-to-pick list) without a filter box.
    if (this.hasSearchTarget) this.searchTarget.value = ''
    if (this.remoteUrlValue) this.remoteQuery = ''
    this.openUp = null // chosen afresh on each open
    // Position as fixed relative to the button so the panel escapes any scroll
    // container (e.g. a tall modal <dialog>) that would otherwise clip it.
    this.renderList('')
    this.boundReposition = () => this.positionPanel()
    window.addEventListener('scroll', this.boundReposition, true)
    window.addEventListener('resize', this.boundReposition)
    if (this.hasSearchTarget) this.searchTarget.focus()
  }

  // Re-run whenever the list changes, not only on open: an upward panel that
  // shrank as the admin typed stayed where its top had been, floating far above
  // its field (independent QA). While typing (keepSide) the panel stays on the
  // side it opened on, so it never jumps across the field mid-search; a scroll
  // or resize picks the side again. The list is cut to the room there is, so it
  // never covers the page header or leaves the screen.
  positionPanel(keepSide = false) {
    const panel = this.panelTarget
    if (panel.hidden) return
    const rect = this.buttonTarget.getBoundingClientRect()
    const gap = 4
    const edge = 8
    panel.style.position = 'fixed'
    // At least the field's width; wider when asked (long option labels), never past the screen.
    const width = Math.min(Math.max(rect.width, this.panelMinWidthValue || 0), window.innerWidth - 2 * edge)
    panel.style.width = `${width}px`
    panel.style.marginTop = '0'
    this.listTarget.style.maxHeight = ''

    const natural = panel.offsetHeight
    const roomBelow = window.innerHeight - edge - rect.bottom - gap
    const roomAbove = rect.top - gap - this.topLimit(edge)
    if (!keepSide || this.openUp === null || this.openUp === undefined) {
      this.openUp = roomBelow < natural && roomAbove > roomBelow
    }
    const room = this.openUp ? roomAbove : roomBelow
    if (natural > room) {
      const list = this.listTarget.offsetHeight
      this.listTarget.style.maxHeight = `${Math.max(72, list - (natural - room))}px`
    }

    const height = panel.offsetHeight
    const top = this.openUp ? rect.top - gap - height : rect.bottom + gap
    this.place(panel, Math.max(edge, Math.min(rect.left, window.innerWidth - edge - width)), top)
  }

  // An ancestor with transform, filter, containment, container-type… becomes
  // the containing block of a fixed box and shifts it. Rather than guess which
  // properties do that, place the panel, measure where it really landed and
  // correct by the difference.
  place(panel, left, top) {
    panel.style.left = `${left}px`
    panel.style.top = `${top}px`
    const landed = panel.getBoundingClientRect()
    panel.style.left = `${2 * left - landed.left}px`
    panel.style.top = `${2 * top - landed.top}px`
  }

  // The panel may not rise over the sticky page header; inside a modal
  // <dialog> (drawn above the header) only the screen edge counts.
  topLimit(edge) {
    if (this.element.closest('dialog')) return edge
    const header = document.querySelector('header')
    const bottom = header ? header.getBoundingClientRect().bottom : 0
    return Math.max(edge, bottom)
  }

  close() {
    this.panelTarget.hidden = true
    this.openUp = null
    if (this.boundReposition) {
      window.removeEventListener('scroll', this.boundReposition, true)
      window.removeEventListener('resize', this.boundReposition)
      this.boundReposition = null
    }
  }

  filter() {
    if (this.remoteUrlValue) return this.remoteSearch(this.searchTarget.value.trim())
    this.renderList(this.searchTarget.value.trim().toLowerCase())
  }

  // With `remoteUrl` the options come from the server as the user types
  // (?q=…, answered with [[label, value], …]) instead of being embedded in the
  // page — a list of every customer company, each with its owner's email,
  // weighed a megabyte and exposed them all (independent QA). The selected
  // option stays in the <select>, so the label and the form value are kept.
  remoteSearch(query) {
    clearTimeout(this.remoteTimer)
    this.remoteQuery = query
    if (query.length < 2) {
      this.sharedOptions = []
      this.refreshOptions()
      return this.renderList('')
    }
    this.remoteTimer = setTimeout(async () => {
      this.remoteAbort?.abort()
      this.remoteAbort = new AbortController()
      try {
        const url = new URL(this.remoteUrlValue, window.location.origin)
        url.searchParams.set('q', query)
        const response = await fetch(url, { headers: { Accept: 'application/json' }, signal: this.remoteAbort.signal })
        if (!response.ok || this.remoteQuery !== query) return
        const rows = await response.json()
        this.sharedOptions = rows.map(([label, value]) => ({ value: String(value), label: String(label), blank: false }))
        this.refreshOptions()
        this.renderList('')
      } catch (_e) {
        // Aborted by a newer keystroke, or offline: the next keystroke retries.
      }
    }, 250)
  }

  choose(event) {
    const value = event.currentTarget.dataset.value
    this.ensureSelectOption(value)
    this.selectTarget.value = value
    this.selectTarget.dispatchEvent(new Event('change', { bubbles: true }))
    this.renderLabel()
    this.close()
  }

  renderList(query) {
    this.fillList(query)
    this.positionPanel(true)
  }

  fillList(query) {
    if (this.remoteUrlValue && (this.remoteQuery || '').length < 2) {
      this.listTarget.innerHTML =
        `<div class="px-3 py-2 text-sm text-gray-400">${this.escape(this.element.dataset.searchableSelectTypeHint || '')}</div>`
      return
    }
    // A remote list shows only what the server matched; the selected option
    // stays in this.options for its label but isn't a search result.
    const pool = this.remoteUrlValue ? (this.sharedOptions || []) : this.options
    // blankChoice: the blank option ("Kein Partner …") is a real choice, listed so it can be picked again. Elsewhere a
    // blank is only the placeholder.
    const matches = pool.filter(
      (o) => (!o.blank || (this.blankChoiceValue && o.label.trim() !== '')) && (!query || o.label.toLowerCase().includes(query))
    )
    if (matches.length === 0) {
      this.listTarget.innerHTML =
        `<div class="px-3 py-2 text-sm text-gray-400">${this.escape(this.noMatchesText())}</div>`
      return
    }
    const current = this.selectTarget.value
    this.listTarget.innerHTML = matches
      .map((o) => {
        const active = o.value === current
          ? 'bg-gray-100 font-medium dark:bg-white/10'
          : ''
        return `<button type="button" data-action="click->searchable-select#choose" data-value="${this.escape(o.value)}" class="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-white/10 ${active}">${this.escape(o.label)}</button>`
      })
      .join('')
  }

  renderLabel() {
    const selected = this.options.find((o) => o.value === this.selectTarget.value && !o.blank)
    this.labelTarget.textContent = selected ? selected.label : (this.placeholderValue || i18n.t('js.searchable_select.placeholder'))
    this.labelTarget.classList.toggle('text-gray-400', !selected)
  }

  onDocClick(event) {
    if (!this.element.contains(event.target)) this.close()
  }

  noMatchesText() {
    return this.element.dataset.searchableSelectNoMatches || i18n.t('js.common.no_matches')
  }

  escape(value) {
    return String(value).replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
    )
  }
}
